import { Button } from "@design-system/Button/Button";
import { RevertIcon } from "@icons";
import React, { useCallback } from "react";
import styles from "./BalanceSheetImporter.module.scss";
import { AccountProxyObject, BalanceSheetRowAction } from "./types";

interface Props {
    row: AccountProxyObject;
    rowIdx: number;
    onUpdateRow: (rowIdx: number, updates: Partial<AccountProxyObject>) => void;
    onCreateAccount: (rowIdx: number) => void;
    isMapping?: boolean;
}

export const BalanceSheetImporterAction: React.FC<Props> = ({
    row,
    rowIdx,
    onUpdateRow,
    onCreateAccount,
    isMapping,
}) => {
    const hasName = row.name?.trim();
    const hasMappedAccount = row.mappedKickAccountId;
    const action = row.action;
    const isActionAccepted = row.isActionAccepted;

    const needsAction = hasName && !hasMappedAccount;
    const wasAccountCreated = hasName && hasMappedAccount && isActionAccepted;
    const isProcessing = isMapping && hasName && !hasMappedAccount;

    const handleCreateAccount = useCallback(() => {
        onCreateAccount(rowIdx);
    }, [rowIdx, onCreateAccount]);

    const undoAction = useCallback(() => {
        onUpdateRow(rowIdx, {
            isActionAccepted: false,
            mappedKickAccountId: null,
        });
    }, [rowIdx, onUpdateRow]);

    const renderActionContent = () => {
        if (wasAccountCreated) {
            return (
                <>
                    <span className={styles.pairActionMessage}>
                        Account created
                    </span>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={undoAction}
                        iconOnly
                        aria-label="Revert account creation"
                    >
                        <RevertIcon />
                    </Button>
                </>
            );
        }

        if (isProcessing) {
            return (
                <span className={styles.pairActionMessage}>Processing...</span>
            );
        }

        if (!needsAction) {
            return null;
        }

        if (action === BalanceSheetRowAction.CreateAccount) {
            return (
                <span className={styles.pairActionMessage}>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleCreateAccount}
                    >
                        Create account
                    </Button>
                </span>
            );
        }

        return null;
    };

    return <>{renderActionContent()}</>;
};
